import { useEffect, useState } from "react";
import { Loader2, UserCog, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageHeader";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Assignments() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [userId, setUserId] = useState("");
  const [assignedProjects, setAssignedProjects] = useState(new Set());
  const [assignedStores, setAssignedStores] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/assignments/bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "assignment_upload_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };
  const doPreview = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/assignments/bulk/preview", fd); setPreview(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const doApply = async () => {
    if (!preview?.batch_id || busy) return; setBusy(true);
    try {
      const { data } = await api.post("/assignments/bulk/apply", { batch_id: preview.batch_id });
      toast.success(`Applied: +${data.project_links_new} project links, +${data.store_links_new} store links`);
      setBulkOpen(false); setPreview(null);
      if (userId) loadAssignments(userId);
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/users", { params: { page_size: 500 } }).then((r) => setUsers((r.data.items || []).filter((u) => !u.is_admin)));
    api.get("/projects", { params: { page_size: 500 } }).then((r) => setProjects(r.data.items || []));
    api.get("/stores", { params: { page_size: 500 } }).then((r) => setStores(r.data.items || []));
  }, []);

  const loadAssignments = async (uid) => {
    setUserId(uid);
    if (!uid) return;
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/assignments/user-projects", { params: { user_id: uid } }),
        api.get("/assignments/user-stores", { params: { user_id: uid } }),
      ]);
      setAssignedProjects(new Set(p.data.project_ids || []));
      setAssignedStores(new Set(s.data.store_ids || []));
    } finally { setLoading(false); }
  };

  const toggleProject = async (pid) => {
    const isAssigned = assignedProjects.has(pid);
    try {
      if (isAssigned) {
        await api.delete("/assignments/user-projects", { data: { user_id: userId, project_id: pid } });
        setAssignedProjects((s) => { const n = new Set(s); n.delete(pid); return n; });
        // cascade removes stores of that project locally
        setAssignedStores((s) => {
          const n = new Set(s);
          stores.filter((st) => st.project_id === pid).forEach((st) => n.delete(st.id));
          return n;
        });
      } else {
        await api.post("/assignments/user-projects", { user_id: userId, project_id: pid });
        setAssignedProjects((s) => new Set(s).add(pid));
      }
      toast.success("Project assignment updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleStore = async (sid) => {
    const isAssigned = assignedStores.has(sid);
    try {
      if (isAssigned) {
        await api.delete("/assignments/user-stores", { data: { user_id: userId, store_id: sid } });
        setAssignedStores((s) => { const n = new Set(s); n.delete(sid); return n; });
      } else {
        await api.post("/assignments/user-stores", { user_id: userId, store_id: sid });
        setAssignedStores((s) => new Set(s).add(sid));
      }
      toast.success("Store assignment updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const eligibleStores = stores.filter((st) => assignedProjects.has(st.project_id));
  const selectedUser = users.find((u) => u.id === userId);
  const isFieldOnly = selectedUser?.is_field_user && !selectedUser?.is_admin;

  return (
    <div>
      <div className="flex items-center justify-between">
        <PageHeader title="User Assignments" subtitle="Assign users to projects (many-to-many) and stores within their projects" />
        <Button variant="outline" size="sm" onClick={() => { setPreview(null); setBulkOpen(true); }} data-testid="bulk-assign-btn"><Upload className="mr-1.5 h-4 w-4" /> Bulk Assign</Button>
      </div>

      <div className="mb-6 max-w-md">
        <Label>Select User</Label>
        <SearchableSelect testid="assignment-user-select" value={userId} onChange={loadAssignments}
          options={users.map((u) => ({ value: u.id, label: `${u.full_name} — ${u.email}`, sub: u.mobile }))}
          placeholder="Choose a user to manage" className="mt-1" />
      </div>

      {!userId ? (
        <div className="grid place-items-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-16 text-slate-400">
          <UserCog className="mb-2 h-8 w-8" />
          <span className="text-sm">Select a user to manage assignments</span>
        </div>
      ) : loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 font-display text-base font-semibold text-slate-900">Projects <span className="ml-1 text-sm font-normal text-slate-400">({assignedProjects.size} assigned)</span></h3>
            <div className="space-y-1" data-testid="assignment-projects">
              {projects.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Checkbox data-testid={`assign-project-${p.id}`} checked={assignedProjects.has(p.id)} onCheckedChange={() => toggleProject(p.id)} />
                    <span className="text-sm text-slate-700">{p.project_name}</span>
                  </div>
                  <Badge variant="secondary" className="font-mono text-[10px]">{p.project_code}</Badge>
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 font-display text-base font-semibold text-slate-900">Stores <span className="ml-1 text-sm font-normal text-slate-400">(within assigned projects)</span></h3>
            <div className="space-y-1" data-testid="assignment-stores">
              {isFieldOnly ? (
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-500" data-testid="assignment-stores-fielduser-note">
                  Field users work from materials allocated to them — no store assignment needed.
                </div>
              ) : eligibleStores.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">Assign a project first to enable its stores</div>
              ) : eligibleStores.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Checkbox data-testid={`assign-store-${s.id}`} checked={assignedStores.has(s.id)} onCheckedChange={() => toggleStore(s.id)} />
                    <span className="text-sm text-slate-700">{s.store_name}</span>
                  </div>
                  <Badge variant="secondary" className="font-mono text-[10px]">{s.project_name}</Badge>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={(o) => !busy && setBulkOpen(o)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Bulk Assign Users</DialogTitle></DialogHeader>
          {!preview && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
            <div className="mb-3">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-assign-template-btn"><Download className="mr-1.5 h-4 w-4" /> Download Template</Button>
              <p className="mt-2 text-xs text-slate-500">Columns: <b>User Email</b>, <b>Project Code</b>, <b>Store Code</b> (optional). One row per user-project (add a store to also grant store access).</p>
            </div>
            <input id="bulkassign" type="file" accept=".xlsx" className="hidden" onChange={doPreview} data-testid="bulk-assign-file" />
            <label htmlFor="bulkassign" className="cursor-pointer text-sm text-blue-600">{busy ? "Reading…" : "Choose an .xlsx file (Preview only)"}</label>
          </div>}
          {preview && <div>
            <div className="mb-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4" data-testid="bulk-assign-summary">
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-slate-800">{preview.total}</div><div className="text-xs text-slate-500">Rows</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-emerald-600">{preview.valid}</div><div className="text-xs text-slate-500">Valid</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-amber-600">{preview.invalid}</div><div className="text-xs text-slate-500">Invalid</div></div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-2"><div className="text-lg font-semibold text-blue-600">{preview.project_links} / {preview.store_links}</div><div className="text-xs text-slate-500">Proj / Store links</div></div>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border text-xs">
              <table className="w-full"><thead className="sticky top-0 bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">User</th><th className="p-2 text-left">Project</th><th className="p-2 text-left">Store</th><th className="p-2 text-left">Status</th></tr></thead>
                <tbody>{preview.rows.map((r) => <tr key={r.row} className="border-t"><td className="p-2">{r.row}</td><td className="p-2">{r.email}{r.user_name ? ` (${r.user_name})` : ""}</td><td className="p-2">{r.project_code}{r.project_name ? ` — ${r.project_name}` : ""}</td><td className="p-2">{r.store_code || "—"}</td><td className={`p-2 ${r.status === "valid" ? "text-emerald-600" : "text-red-600"}`}>{r.status === "valid" ? "Valid" : r.error}</td></tr>)}</tbody></table>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setBulkOpen(false); }}>Cancel</Button>
            {preview && preview.valid > 0 && <Button disabled={busy} onClick={doApply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-assign-apply-btn">Apply {preview.valid} Assignment(s)</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
